import { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';

export type TerminalMode = 'local' | 'ssh';

export interface SSHConfig {
  host: string;
  port: number;
  username: string;
  password?: string;
}

const SSH_GATEWAY =
  process.env.NODE_ENV === 'development' ? 'ws://localhost:8222' : 'ws://localhost:8222';

export function useTerminal(mode: TerminalMode, sshConfig?: SSHConfig) {
  const termRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);

  function getWsUrl(): string {
    if (mode === 'ssh' && sshConfig) {
      const params = new URLSearchParams({
        host: sshConfig.host,
        port: String(sshConfig.port),
        username: sshConfig.username,
      });
      if (sshConfig.password) params.set('password', sshConfig.password);
      return `${SSH_GATEWAY}/terminal/ssh?${params}`;
    }
    return `${SSH_GATEWAY}/terminal/local`;
  }

  useEffect(() => {
    if (!termRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      theme: {
        background: '#0d1117',
        foreground: '#c9d1d9',
        cursor: '#58a6ff',
        selectionBackground: '#264f78',
      },
      allowTransparency: true,
      convertEol: true,
      cols: 80,
      rows: 24,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(new WebLinksAddon());
    fitAddonRef.current = fitAddon;

    term.open(termRef.current);
    fitAddon.fit();

    terminalRef.current = term;

    function connect() {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        term.clear();
        term.focus();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'data') term.write(atob(msg.data));
          else if (msg.type === 'connected') term.write(`\r\n\x1b[32m✓ ${msg.message}\x1b[0m\r\n`);
          else if (msg.type === 'error') term.write(`\r\n\x1b[31m✗ ${msg.message}\x1b[0m\r\n`);
          else if (msg.type === 'close' || msg.type === 'exit') {
            term.write('\r\n\x1b[33m! Session closed\x1b[0m\r\n');
            setConnected(false);
          }
        } catch {
          term.write(event.data);
        }
      };

      ws.onerror = () => {
        term.write('\r\n\x1b[31m✗ Connection error\x1b[0m\r\n');
        setConnected(false);
      };
      ws.onclose = () => setConnected(false);

      term.onData((data) => {
        if (ws.readyState === WebSocket.OPEN)
          ws.send(JSON.stringify({ type: 'input', data: btoa(data) }));
      });
    }

    connect();

    const ro = new ResizeObserver(() => {
      try {
        fitAddon.fit();
      } catch {}
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const dims = fitAddon.proposeDimensions();
        if (dims)
          wsRef.current.send(JSON.stringify({ type: 'resize', cols: dims.cols, rows: dims.rows }));
      }
    });
    if (termRef.current) ro.observe(termRef.current);

    return () => {
      ro.disconnect();
      wsRef.current?.close();
      term.dispose();
    };
  }, [mode, sshConfig?.host, sshConfig?.port, sshConfig?.username]);

  const fitTerminal = useCallback(() => {
    try {
      fitAddonRef.current?.fit();
    } catch {}
  }, []);

  return { connected, termRef, fitTerminal };
}
